import React from "react";

import classnames from "classnames";
import PropTypes from "prop-types";
import { withRouter, Link } from "react-router-dom";
import { withStyleProps } from "Utils/styleProps";

import Icon from "Common/Icon";

import type { LocationShape, RouterHistory, Location, Match } from "react-router-dom";

type Props = {
  /**
  @ignore
  * */
  className?: string | null | undefined;
  color?: "blue" | "gray" | "white" | "link" | "link-white" | "link-black" | "deprecated-link";

  /**
  [Intuit] Use this to provide contrast when using a primary button on a dark background
  * */
  onDarkBackground?: boolean;

  /**
  Prevents the user from being able to click on the button and activating the action.
  * */
  disabled?: boolean;

  /**
   * Indicates that the button is in a loading state, when true, a spinner icon will be shown
   * */
  submitting?: boolean;

  /**
  @ignore
  * */
  round?: boolean;

  /**
  Allows the button to fill the width of it's container.
  * */
  fullWidth?: boolean;
  size?: "small" | "medium" | "large" | "icon";

  /**
  @ignore
  * */
  component?: React.ElementType;

  /**
  @ignore
  * */
  children?: React.ReactNode;

  /**
  @ignore
  * */
  text?: string;

  /**
  @ignore
  * */
  to?: string | LocationShape | Location;

  /**
  @ignore
  * */
  target?: string;

  /**
  @ignore
  * */
  href?: string;

  /**
  @ignore
  * */
  rel?: string;
};

/**
 * Use a button for triggering actions on an object, or indicating actions users can take.
 * forwardRef allows access to the underlying button element (needed for popovers/hover behaviors)
 */
export const UndecoratedButton = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      className,
      color = "blue",
      onDarkBackground,
      component,
      fullWidth,
      submitting = false,
      round,
      size = "small",
      text,
      to,
      href,
      target,
      rel: localRel,
      ...rest
    },
    ref,
  ) => {
    const effectiveColor = color;
    const effectiveSize = size;

    const classes = classnames(
      "btn",
      "Button",
      `Button--${effectiveColor}`,
      `Button--${effectiveSize}`,
      className,
      {
        "Button--fullWidth": fullWidth,
      },
      {
        "Button--round": round,
      },
    );

    if (component) {
      if (component === Link && to) {
        return React.createElement(
          LinkButton,
          {
            className: classes,
            to,
            ...rest,
          },
          children || text,
        );
      }
      return React.createElement(
        component,
        {
          className: classes,
          to,
          ...rest,
        },
        children || text,
      );
    }
    if (href) {
      const relProp = localRel
        ? {
            rel: localRel,
          }
        : {};
      return (
        <a href={rest.disabled ? undefined : href} {...relProp} className="Button__Container" target={target || "_self"}>
          <button ref={ref} type="button" className={classes} {...rest}>
            {children || text}
          </button>
        </a>
      );
    }
    return (
      <button ref={ref} type="button" className={classes} {...rest}>
        <Icon icon="spinner-third" flavor="solid" spacing="right" spin className={submitting ? "" : "hidden"} />
        {children || text}
      </button>
    );
  },
);

type LinkButtonProps = {
  history: RouterHistory;
  location: Location;
  match: Match;
  staticContext?: any;
  to: string | LocationShape;
  onClick: (arg0: unknown) => unknown;
};

// React Router aware button.
// Do not use outside, just create a Button with an underlying Link component.
// @ts-expect-error
const LinkButtonDumb = ({
  history,
  location,
  match,
  staticContext,
  to,
  onClick,
  ...rest
}: LinkButtonProps): React.ReactNode => ( // filtering out props that `button` doesn't know what to do with.
  <button
    {...rest} // `children` is just another prop!
    onClick={(event) => {
      if (onClick) {
        onClick(event);
      }

      history.push(to);
    }}
  />
);

const LinkButton = withRouter(LinkButtonDumb);
LinkButton.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};
export default withStyleProps(UndecoratedButton);
